import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Check } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { api, errorMessage } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useLang } from "../../lib/i18n";

const EMPTY = { name_en: "", name_ar: "", description: "", permissions: [] };
const labelCls = "text-xs font-bold uppercase tracking-wider text-slate-500";

const PermItem = ({ p, checked, allowed, onToggle }) => {
  const { t, pick } = useLang();
  return (
  <label className={`flex items-start gap-4 rounded-2xl border p-4 transition-colors duration-200 ${checked ? "border-slate-900 bg-slate-50" : "border-slate-200/80 bg-white"} ${allowed ? "cursor-pointer hover:border-slate-400" : "opacity-50"}`}>
    <Checkbox checked={checked} disabled={!allowed} onCheckedChange={() => onToggle(p.key)} data-testid={`perm-${p.key}`} className="mt-1" />
    <span className="flex-1">
      <span className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-slate-900">{pick(p.label, p.label_ar)}</span>
        {p.status === "reserved" && <span className="rounded-full bg-philadelphia/30 px-2 py-0.5 text-[10px] font-bold text-slate-700">{t("Coming soon")}</span>}
      </span>
      <span className="mt-1 block text-sm leading-relaxed text-slate-500" data-testid={`perm-help-${p.key}`}>{pick(p.help, p.help_ar)}</span>
      {!allowed && <span className="mt-1 block text-xs text-slate-400">{t("You don't have this permission, so you can't give it.")}</span>}
    </span>
  </label>
  );
};

const PermGroup = ({ group, perms, selected, mine, onToggle }) => {
  const { t, pick } = useLang();
  return (
  <section data-testid={`perm-group-${group}`}>
    <div className="mb-3 flex items-baseline gap-3">
      <h3 className="font-display text-lg font-bold text-slate-900">{pick(group, perms[0].group_ar)}</h3>
      <span className="text-xs font-semibold text-slate-400">{t("{count}/{total} on", { count: perms.filter((p) => selected.has(p.key)).length, total: perms.length })}</span>
    </div>
    <div className="grid gap-3 lg:grid-cols-2">
      {perms.map((p) => <PermItem key={p.key} p={p} checked={selected.has(p.key)} allowed={mine.has(p.key)} onToggle={onToggle} />)}
    </div>
  </section>
  );
};

export const RoleEditor = ({ role, permissions, onDone }) => {
  const { user } = useAuth();
  const { t, pick } = useLang();
  const [form, setForm] = useState(role ? { ...EMPTY, ...role } : EMPTY);
  const [saving, setSaving] = useState(false);
  const mine = new Set(user.permissions);
  const selected = new Set(form.permissions);
  const groups = [...new Set(permissions.map((p) => p.group))];
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (key) => setForm((f) => ({
    ...f, permissions: f.permissions.includes(key) ? f.permissions.filter((k) => k !== key) : [...f.permissions, key],
  }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { name_en: form.name_en, name_ar: form.name_ar, description: form.description, permissions: form.permissions };
    try {
      if (role) await api.patch(`/admin/roles/${role.key}`, body);
      else await api.post("/admin/roles", body);
      toast.success(t(role ? "Role updated" : "Role created"));
      onDone(true);
    } catch (err) { toast.error(errorMessage(err)); } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-8" data-testid="role-editor">
      <div className="flex flex-wrap items-center gap-4">
        <button type="button" onClick={() => onDone(false)} data-testid="role-editor-back"
          className="btn-press inline-flex min-h-[42px] items-center gap-2 rounded-xl border border-slate-300 px-4 text-sm font-semibold hover:border-slate-900">
          <ArrowLeft className="h-4 w-4 rtl:-scale-x-100" /> {t("All roles")}
        </button>
        <h2 className="me-auto font-display text-2xl font-bold text-slate-900">{role ? t("Edit {name}", { name: pick(role.name_en, role.name_ar) }) : t("New role")}</h2>
        <span className="text-sm font-semibold text-slate-500" data-testid="role-editor-count">{t("{count}/{total} permissions on", { count: form.permissions.length, total: permissions.length })}</span>
      </div>
      <div className="grid gap-4 rounded-3xl border border-slate-200/80 bg-white p-5 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-1.5">
          <Label className={labelCls}>{t("Role name (English)")}</Label>
          <Input dir="ltr" required value={form.name_en} placeholder="HR Manager" onChange={set("name_en")} className="h-12 rounded-xl" data-testid="role-form-name_en" />
        </div>
        <div className="space-y-1.5">
          <Label className={labelCls}>{t("Role name (Arabic)")}</Label>
          <Input dir="rtl" value={form.name_ar} placeholder="مدير الموارد البشرية" onChange={set("name_ar")} className="h-12 rounded-xl font-arabic text-right" data-testid="role-form-name_ar" />
        </div>
        <div className="space-y-1.5">
          <Label className={labelCls}>{t("Description")}</Label>
          <Input value={form.description} onChange={set("description")} className="h-12 rounded-xl" data-testid="role-form-description" />
        </div>
      </div>
      {groups.map((g) => (
        <PermGroup key={g} group={g} perms={permissions.filter((p) => p.group === g)} selected={selected} mine={mine} onToggle={toggle} />
      ))}
      <div className="sticky bottom-4 flex justify-end">
        <button type="submit" disabled={saving} data-testid="role-form-submit"
          className="btn-press inline-flex min-h-[50px] items-center gap-2 rounded-2xl bg-cobalt px-8 font-semibold text-white shadow-lg hover:bg-cobalt-hover disabled:opacity-60">
          <Check className="h-4 w-4" /> {saving ? t("Saving…") : t(role ? "Save role" : "Create role")}
        </button>
      </div>
    </form>
  );
};
